import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, Car, CheckCircle2, ScanLine, Send, Info, ImagePlus, X, History } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { URGENCY, STATUS } from "@/lib/constants";
import PartsCatalogue from "@/components/PartsCatalogue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EMPTY_VEHICLE = { marque: "", modele: "", annee: "", motorisation: "", carburant: "", cylindree: "", puissance: "" };

export default function NewRequestForm({ onCreated }) {
  const [tab, setTab] = useState("plate");
  const [value, setValue] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [vehicle, setVehicle] = useState(null); // decoded VehicleInfo
  const [manual, setManual] = useState(EMPTY_VEHICLE);

  const [part, setPart] = useState({ part_name: "", part_reference: "", notes: "" });
  const [urgency, setUrgency] = useState("");
  const [delay, setDelay] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [country, setCountry] = useState("fr");
  const fileRef = useRef(null);

  const COUNTRIES = [
    { code: "fr", label: "France", flag: "🇫🇷" },
    { code: "uk", label: "Royaume-Uni", flag: "🇬🇧" },
    { code: "es", label: "Espagne", flag: "🇪🇸" },
    { code: "it", label: "Italie", flag: "🇮🇹" },
    { code: "pt", label: "Portugal", flag: "🇵🇹" },
  ];

  const examples = { plate: "AA-123-BC", vin: "VF1DZ0N0641118804" };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path, filename: data.filename }]);
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const decode = async () => {
    if (!value.trim()) { toast.error("Saisissez une valeur"); return; }
    setDecoding(true);
    setVehicle(null);
    setHistory([]);
    try {
      const { data } = await api.post("/vehicle/decode", { input_type: tab, value, country: tab === "plate" ? country : "fr" });
      setVehicle(data);
      setManual({
        marque: data.marque || "", modele: data.modele || "", annee: data.annee || "",
        motorisation: data.motorisation || "", carburant: data.carburant || "",
        cylindree: data.cylindree || "", puissance: data.puissance || "",
      });
      if (data.found) toast.success(`${data.marque} ${data.modele} identifié`);
      else toast.info("Véhicule non trouvé — complétez manuellement");
      try {
        const h = await api.get(`/vehicle/history`, { params: { value } });
        setHistory(h.data.requests || []);
      } catch { setHistory([]); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDecoding(false);
    }
  };

  const useExample = () => { setValue(examples[tab]); };

  const submit = async () => {
    if (!vehicle) { toast.error("Décodez d'abord un véhicule"); return; }
    if (!part.part_name.trim()) { toast.error("Indiquez le nom ou la référence de la pièce"); return; }
    if (!urgency) { toast.error("Choisissez l'urgence du besoin"); return; }
    const veh = {
      input_type: tab, input_value: value.toUpperCase(), found: vehicle.found,
      ...manual, type_vehicule: vehicle.type_vehicule || "", source: vehicle.source || "",
      code_moteur: vehicle.code_moteur || "", type_mine: vehicle.type_mine || "",
      vin: vehicle.vin || "", photo_url: vehicle.photo_url || "",
      k_type: vehicle.k_type || "", tecdoc_car_id: vehicle.tecdoc_car_id || "",
      tecdoc_manu_id: vehicle.tecdoc_manu_id || "", tecdoc_model_id: vehicle.tecdoc_model_id || "",
      tires: vehicle.tires || null,
    };
    setSubmitting(true);
    try {
      const { data } = await api.post("/requests", {
        vehicle: veh,
        part_name: part.part_name,
        part_reference: part.part_reference,
        notes: part.notes,
        urgency,
        delay_days: urgency === "delay" ? delay : null,
        photos,
      });
      toast.success(data.email_sent ? "Demande envoyée au fournisseur par email" : "Demande enregistrée");
      // reset
      setValue(""); setVehicle(null); setManual(EMPTY_VEHICLE);
      setPart({ part_name: "", part_reference: "", notes: "" }); setUrgency(""); setDelay(3); setPhotos([]); setHistory([]);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const setM = (k) => (e) => setManual((m) => ({ ...m, [k]: e.target.value }));

  const pickPart = (name) => {
    setPart((p) => ({ ...p, part_name: name }));
    toast.success(`« ${name} » ajouté à la demande`);
    document.querySelector("[data-testid='part-name']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="space-y-6" data-testid="vehicle-search-form">
      {/* Step 1 : vehicle */}
      <div className="p-5 sm:p-7 bg-card border border-border rounded-2xl shadow-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-red-600 via-blue-600 to-blue-800" />
        <div className="flex items-center gap-2 mb-4">
          <span className="h-6 w-6 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center">1</span>
          <h3 className="font-heading font-bold text-lg">Identifier le véhicule</h3>
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setVehicle(null); setValue(""); }}>
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="plate" data-testid="tab-plate">Plaque d'immatriculation</TabsTrigger>
            <TabsTrigger value="vin" data-testid="tab-vin">Numéro de châssis (VIN)</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "plate" && (
          <div className="mt-4" data-testid="country-selector">
            <span className="text-xs text-muted-foreground">Pays d'immatriculation</span>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  data-testid={`country-${c.code}`}
                  onClick={() => { setCountry(c.code); setVehicle(null); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${country === c.code ? "bg-blue-700 text-white border-blue-700" : "border-border hover:border-blue-300"}`}
                >
                  <span>{c.flag}</span>{c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Input
              data-testid="vehicle-input"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              placeholder={tab === "plate" ? "Ex : AA-123-BB" : "Ex : VF7... (17 caractères)"}
              className="h-12 font-mono tracking-widest uppercase text-base"
              onKeyDown={(e) => e.key === "Enter" && decode()}
            />
            {decoding && <div className="scan-line" style={{ top: 0 }} />}
          </div>
          <Button onClick={decode} disabled={decoding} data-testid="decode-btn" className="h-12 bg-blue-700 hover:bg-blue-800 px-6">
            {decoding ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Search className="h-5 w-5 mr-2" />Décoder</>}
          </Button>
        </div>
        <button onClick={useExample} data-testid="example-btn" className="mt-2 text-sm text-blue-600 hover:underline flex items-center gap-1">
          <ScanLine className="h-3.5 w-3.5" /> Utiliser un exemple ({examples[tab]})
        </button>

        <AnimatePresence>
          {vehicle && (
            <motion.div
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              data-testid="decoded-vehicle-card" className="mt-5 overflow-hidden"
            >
              <div className={`rounded-xl border p-5 ${vehicle.found ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                <div className="flex items-center gap-2 mb-3">
                  {vehicle.found ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Info className="h-5 w-5 text-amber-600" />}
                  <span className="font-semibold">{vehicle.found ? "Véhicule identifié" : "Saisie manuelle requise"}</span>
                  {vehicle.source && <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1"><Car className="h-3.5 w-3.5" />{vehicle.source}</span>}
                </div>
                {!vehicle.found && <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">{vehicle.message}</p>}
                <div className="flex gap-4">
                  {vehicle.photo_url && (
                    <div className="shrink-0 h-24 w-32 rounded-lg overflow-hidden border border-border bg-white hidden sm:block">
                      <img src={vehicle.photo_url} alt={`${vehicle.marque} ${vehicle.modele}`} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-1">
                    {[
                      ["marque", "Marque"], ["modele", "Modèle"], ["annee", "Année"],
                      ["motorisation", "Motorisation"], ["carburant", "Carburant"], ["puissance", "Puissance"],
                    ].map(([k, lbl]) => (
                      <div key={k}>
                        <Label className="text-xs text-muted-foreground">{lbl}</Label>
                        <Input data-testid={`veh-${k}`} value={manual[k]} onChange={setM(k)} className="mt-1 h-9" placeholder="—" />
                      </div>
                    ))}
                  </div>
                </div>
                {(vehicle.code_moteur || vehicle.type_mine || vehicle.vin) && (
                  <div className="flex flex-wrap gap-2 mt-3 text-xs">
                    {vehicle.code_moteur && <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-700 font-mono">Code moteur : {vehicle.code_moteur}</span>}
                    {vehicle.type_mine && <span className="px-2 py-1 rounded-md bg-slate-500/10 text-slate-600 font-mono">Type mine : {vehicle.type_mine}</span>}
                    {vehicle.vin && <span className="px-2 py-1 rounded-md bg-slate-500/10 text-slate-600 font-mono">VIN : {vehicle.vin}</span>}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {vehicle && history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="vehicle-history" className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <History className="h-4 w-4 text-blue-600" />
                <span className="font-semibold text-sm">Historique de ce véhicule — {history.length} demande(s) précédente(s)</span>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {history.map((h) => {
                  const hu = URGENCY[h.urgency] || URGENCY.stock;
                  const hs = STATUS[h.status] || STATUS.pending;
                  return (
                    <div key={h.id} data-testid={`history-item-${h.id}`} className="flex items-center justify-between gap-2 bg-card rounded-lg border border-border px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{h.part_name}{h.part_reference ? ` · ${h.part_reference}` : ""}</div>
                        <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${hu.badge}`}>{hu.short}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${hs.cls}`}>{hs.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {vehicle && vehicle.found && <PartsCatalogue vehicle={vehicle} onPick={pickPart} />}
      </div>

      {/* Step 2 : part + urgency */}
      <div className={`p-5 sm:p-7 bg-card border border-border rounded-2xl shadow-sm transition-opacity ${vehicle ? "opacity-100" : "opacity-50 pointer-events-none"}`} data-testid="part-request-form">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-6 w-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">2</span>
          <h3 className="font-heading font-bold text-lg">Pièce recherchée & urgence</h3>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pn">Nom de la pièce *</Label>
            <Input id="pn" data-testid="part-name" value={part.part_name} onChange={(e) => setPart({ ...part, part_name: e.target.value })} placeholder="Ex : Kit embrayage bi-masse" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="pr">Référence constructeur (OEM)</Label>
            <Input id="pr" data-testid="part-ref" value={part.part_reference} onChange={(e) => setPart({ ...part, part_reference: e.target.value })} placeholder="Ex : 0445110" className="mt-1.5 font-mono" />
          </div>
        </div>

        <div className="mt-5">
          <Label className="mb-2 block">Urgence du besoin *</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.values(URGENCY).map((u) => {
              const Icon = u.icon;
              const active = urgency === u.key;
              return (
                <button
                  key={u.key}
                  data-testid={`urgency-${u.key}`}
                  onClick={() => setUrgency(u.key)}
                  className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${active ? "border-blue-600 bg-blue-500/5 shadow-sm" : "border-border hover:border-blue-300"}`}
                >
                  <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${u.badge}`}><Icon className="h-5 w-5" /></span>
                  <div>
                    <div className="font-semibold text-sm">{u.label}</div>
                    <div className="text-xs text-muted-foreground">{u.key === "urgent" ? "Livraison sous 24h" : u.key === "stock" ? "Disponible immédiatement" : "Sous 1 à 7 jours"}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {urgency === "delay" && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/5 p-5">
                <div className="flex items-center justify-between mb-3">
                  <Label>Délai acceptable</Label>
                  <span data-testid="delay-value" className="font-mono font-bold text-blue-700 text-lg">{delay} jour{delay > 1 ? "s" : ""}</span>
                </div>
                <Slider data-testid="delay-slider" min={1} max={7} step={1} value={[delay]} onValueChange={(v) => setDelay(v[0])} />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => <span key={n}>{n}</span>)}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4">
          <Label>Photos de la pièce (optionnel)</Label>
          <div className="mt-1.5 flex flex-wrap gap-3">
            {photos.map((p) => (
              <div key={p.path} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border group">
                <img src={fileUrl(p.path)} alt={p.filename} className="h-full w-full object-cover" />
                <button type="button" data-testid="remove-photo" onClick={() => removePhoto(p.path)}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button type="button" data-testid="add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="h-20 w-20 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-blue-400 hover:text-blue-600 transition-colors">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImagePlus className="h-5 w-5" /><span className="text-[10px] mt-1">Ajouter</span></>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="photo-input" onChange={onPickPhotos} />
          </div>
        </div>

        <div className="mt-4">
          <Label htmlFor="notes">Notes complémentaires</Label>
          <Textarea id="notes" data-testid="part-notes" value={part.notes} onChange={(e) => setPart({ ...part, notes: e.target.value })} placeholder="Précisions, état neuf/occasion, côté gauche/droit..." className="mt-1.5" />
        </div>

        <Button onClick={submit} disabled={submitting} data-testid="submit-request-btn" className="w-full mt-6 h-12 bg-red-600 hover:bg-red-700 text-base">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5 mr-2" />Envoyer la demande</>}
        </Button>
      </div>
    </div>
  );
}
